import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCohortEnrollment } from './useParticipantData';

// -------- Types --------

export interface ScheduleExpert {
  id: string;
  name: string;
  photo_url: string | null;
  profile: string | null;
  linkedin_url: string | null;
}

// Internal interface for the profile join
interface ProfileJoin {
  id: string;
  full_name: string;
  avatar_url: string | null;
  bio: string | null;
  linkedin_url: string | null;
}

export interface ScheduleItem {
  id: string;
  cohort_id: string;
  type: string | null;
  title: string | null;
  scheduled_date: string | null;
  start_hour: number | null;
  start_minute: number | null;
  end_hour: number | null;
  end_minute: number | null;
  week_number: number | null;
  connection_link: string | null;
  recording_link: string | null;
  meet_recording_link: string | null;
  ref_id: string | null;
  expert_id: string | null;
  expert: ScheduleExpert | null;
  // Joined from program_workshops via ref_id
  workshop?: {
    id: string;
    title: string;
    description: string | null;
    material_link: string | null;
    problema_concreto: string | null;
    resultado_esperado: string | null;
    tiempo_estimado: string | null;
    tip_adicional: string | null;
    requires_deliverable: boolean | null;
    duration: string | null;
    deliverable_resource_url: string | null;
  } | null;
  mentorship?: {
    id: string;
    title: string;
    description: string | null;
    duration: string | null;
  } | null;
}

// -------- Hooks --------

/** Fetch all schedule items for a given cohort (or auto-detect from enrollment) */
export function useCohortSchedule(explicitCohortId?: string | null) {
  const enrollment = useCohortEnrollment();
  const enrolledCohortId = enrollment.data?.cohort_id as string | undefined;
  const cohortId = explicitCohortId ?? enrolledCohortId;

  return useQuery({
    queryKey: ['cohort-schedule', cohortId],
    queryFn: async (): Promise<ScheduleItem[]> => {
      // Fetch schedule items with expert join
      const { data, error } = await supabase
        .from('cohort_schedule_items')
        .select('*, profiles!cohort_schedule_items_expert_id_fkey(id, full_name, avatar_url, bio, linkedin_url)')
        .eq('cohort_id', cohortId!)
        .order('scheduled_date', { ascending: true });

      if (error) throw error;
      if (!data || data.length === 0) return [];

      // Gather ref_ids that are workshops to join workshop details
      const workshopRefIds = data
        .filter((item) => item.type === 'workshop' && item.ref_id)
        .map((item) => item.ref_id!)
        .filter(Boolean);

      let workshopMap: Record<string, ScheduleItem['workshop']> = {};
      if (workshopRefIds.length > 0) {
        const { data: workshops, error: wsErr } = await supabase
          .from('program_workshops')
          .select('id, title, description, material_link, problema_concreto, resultado_esperado, tiempo_estimado, tip_adicional, requires_deliverable, duration, deliverable_resource_url')
          .in('id', workshopRefIds)
          .is('deleted_at', null);

        if (!wsErr && workshops) {
          workshops.forEach((ws) => {
            workshopMap[ws.id] = ws;
          });
        }
      }

      // Gather ref_ids for mentorships (accept both 'mentoring' and 'mentorship')
      const mentorshipRefIds = data
        .filter((item) => (item.type === 'mentoring' || item.type === 'mentorship') && item.ref_id)
        .map((item) => item.ref_id!)
        .filter(Boolean);

      let mentorshipMap: Record<string, { id: string; title: string; description: string | null; duration: string | null }> = {};
      if (mentorshipRefIds.length > 0) {
        const { data: mentorships, error: msErr } = await supabase
          .from('program_mentorships')
          .select('id, title, description, duration')
          .in('id', mentorshipRefIds)
          .is('deleted_at', null);

        if (!msErr && mentorships) {
          mentorships.forEach((ms) => {
            mentorshipMap[ms.id] = ms;
          });
        }
      }

      return data.map((item: any) => {
        const isMentoring = item.type === 'mentoring' || item.type === 'mentorship';
        // Complementary mentorship (no ref_id) → use the schedule item's own fields
        const fallbackMentorship = isMentoring
          ? {
              id: item.id,
              title: item.title ?? 'Mentoría',
              description: item.description ?? null,
              duration: null,
            }
          : null;
        return {
          id: item.id,
          cohort_id: item.cohort_id,
          type: item.type,
          title: item.title,
          scheduled_date: item.scheduled_date,
          start_hour: item.start_hour,
          start_minute: item.start_minute,
          end_hour: item.end_hour,
          end_minute: item.end_minute,
          week_number: item.week_number,
          connection_link: item.connection_link,
          recording_link: item.recording_link,
          meet_recording_link: item.meet_recording_link ?? null,
          ref_id: item.ref_id,
          expert_id: item.expert_id,
          expert: item.profiles ? {
            id: (item.profiles as any).id,
            name: (item.profiles as any).full_name,
            photo_url: (item.profiles as any).avatar_url,
            profile: (item.profiles as any).bio,
            linkedin_url: (item.profiles as any).linkedin_url,
          } : null,
          workshop: item.ref_id ? workshopMap[item.ref_id] ?? null : null,
          mentorship: isMentoring
            ? (item.ref_id ? (mentorshipMap[item.ref_id] ?? fallbackMentorship) : fallbackMentorship)
            : null,
        };
      });

    },
    enabled: !!cohortId,
  });
}

/** Fetch workshop tools for specific workshop IDs */
export function useWorkshopTools(workshopIds: string[]) {
  return useQuery({
    queryKey: ['workshop-tools', workshopIds],
    queryFn: async () => {
      if (workshopIds.length === 0) return {};
      const { data, error } = await supabase
        .from('workshop_tools')
        .select('workshop_id, tool_name')
        .in('workshop_id', workshopIds);

      if (error) throw error;

      const map: Record<string, string[]> = {};
      (data ?? []).forEach((wt) => {
        if (!map[wt.workshop_id]) map[wt.workshop_id] = [];
        map[wt.workshop_id].push(wt.tool_name);
      });
      return map;
    },
    enabled: workshopIds.length > 0,
  });
}
